import styled from '@emotion/styled';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import { useCustomSearchParams, usePrevious } from '@selfserve/react-hooks';
import { responseTypes } from '@selfserve/react-utils';

import { useSortByUrl } from '../hooks/use-sort-by-url';

import { IContextData } from './admin-layout';

import { useSubscriptionsRequest } from '../data';

import { IBeFilter } from '../types';

import { getAdminSearchValue } from '../utils/get-admin-search-value';

import { StyledPageTitle } from '../ui/admin-styled-components';
import { AdminSpinner } from '../ui/admin-spinner';
import { AdminSubscriptionsList } from './admin-subscriptions-list';

const StyledAdminSubscriptionsContainer = styled('div')`
  height: 100%;
`;

export const AdminSubscriptions = () => {
  const { t } = useTranslation();
  const [query] = useCustomSearchParams();
  const { openErrorModal } = useOutletContext<IContextData>() || {};
  const { sortValue, sortType } = useSortByUrl();
  const [beFilter, setBeFilter] = useState<null | IBeFilter>(null);
  const { data, refetch, isLoading } = useSubscriptionsRequest({
    pageSize: +query['pageSize'],
    pageIndex: +query['pageIndex'] - 1,
    sortByItems: "created_at",
    sortByDirection: "dsc",
    ...(beFilter && beFilter),
    ...(query['status'] && {
      status: (query['status'] as string[])?.join(','),
    }),
    ...(sortValue && {
      sortByItems: sortValue,
    }),
    ...(sortType && {
      sortByDirection: sortType,
    }),
  });

  const prevData = usePrevious(data);
  const subscriptions = useMemo(() => {
    const latestData = data || prevData;
    if (latestData?.data && Array.isArray(latestData?.data?.subscriptions)) {
      return latestData?.data?.subscriptions;
    }
    return [];
  }, [data]);
  const summary = useMemo(() => {
    const latestData = data || prevData;
    if (latestData?.data && Object.keys(latestData?.data?.summary || {}).length) {
      return latestData?.data?.summary;
    }
    return {};
  }, [data]);

  useEffect(() => {
    if (
      data?.status &&
      !String(data?.status).startsWith(responseTypes.SUCCESS)
    ) {
      openErrorModal({ text: data?.data?.message });
    }
  }, [data]);

  return (
    <StyledAdminSubscriptionsContainer>
      <StyledPageTitle isSmall>
        {t('common.admin-subscriptions')}
      </StyledPageTitle>
      <AdminSubscriptionsList
        handleSetBeFilter={getAdminSearchValue(setBeFilter)}
        subscriptions={subscriptions}
        summary={summary}
        refetch={refetch}
      />
      <AdminSpinner loading={isLoading} fullScreen />
    </StyledAdminSubscriptionsContainer>
  );
};
